import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './MpdId.module.css';

export default function MpdId() {
  const { currentUser } = useAuth();

  return (
    <>
      <Helmet>
        <title>MPD ID Page | Your Shareable Public Profile</title>
        <meta
          name="description"
          content="Create your MPD ID Page: a clean, shareable public profile you control. Own your digital identity with a single link you can share anywhere."
        />
      </Helmet>

      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          {currentUser && (
            <div className={styles.userButtonContainer}>
              <Link to="/user-profile" className={styles.secondaryButton}>
                Manage my ID Page
              </Link>
            </div>
          )}

          <header className={styles.hero}>
            <h1 className={styles.title}>MPD ID Page</h1>
            <p className={styles.subtitle}>
              A beautiful, web-based public profile you can share anywhere.
            </p>

            <div className={styles.heroCard}>
              <div className={styles.heroCopy}>
                <p className={styles.lead}>
                  Stop sending scattered links. Share one page that clearly tells people who you are
                  and how to reach you.
                </p>
                <ul className={styles.bullets}>
                  <li>One link you control</li>
                  <li>Public profile URL: /profile/your-id</li>
                  <li>Built for real-world sharing (email, business cards, socials)</li>
                  <li>Update anytime</li>
                </ul>

                <div className={styles.ctaRow}>
                  <Link to="/buy?product=mpdId" className={styles.primaryButton}>
                    Get MPD ID Page
                  </Link>
                  {!currentUser ? (
                    <Link to="/signup" className={styles.secondaryButton}>
                      Create account
                    </Link>
                  ) : (
                    <Link to="/id" className={styles.secondaryButton}>
                      Learn about MPD ID
                    </Link>
                  )}
                </div>

                <div className={styles.trustRow}>
                  <div className={styles.trustItem}>
                    <div className={styles.trustLabel}>Privacy-first</div>
                    <div className={styles.trustValue}>You decide what to share</div>
                  </div>
                  <div className={styles.trustItem}>
                    <div className={styles.trustLabel}>Fast setup</div>
                    <div className={styles.trustValue}>Start in minutes</div>
                  </div>
                  <div className={styles.trustItem}>
                    <div className={styles.trustLabel}>Subscription</div>
                    <div className={styles.trustValue}>Keep your page active</div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What you get</h2>
            <div className={styles.featureGrid}>
              <div className={styles.featureCard}>
                <h3 className={styles.featureTitle}>A professional public profile</h3>
                <p className={styles.featureText}>
                  A clean page that looks great on mobile and desktop. Share it confidently.
                </p>
              </div>
              <div className={styles.featureCard}>
                <h3 className={styles.featureTitle}>A shareable, stable URL</h3>
                <p className={styles.featureText}>
                  Your public link lives at <span className={styles.code}>/profile/your-id</span>.
                </p>
              </div>
              <div className={styles.featureCard}>
                <h3 className={styles.featureTitle}>Simple control over visibility</h3>
                <p className={styles.featureText}>
                  Publish only what you want. Keep the rest private.
                </p>
              </div>
              <div className={styles.featureCard}>
                <h3 className={styles.featureTitle}>A better “link in bio”</h3>
                <p className={styles.featureText}>
                  Great for networking, recruiting, introductions, and meeting new people.
                </p>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>How it works</h2>
            <div className={styles.steps}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepBody}>
                  <div className={styles.stepTitle}>Create your account</div>
                  <div className={styles.stepText}>
                    Sign up and you’ll have access to your profile editor.
                  </div>
                </div>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepBody}>
                  <div className={styles.stepTitle}>Publish what you want to share</div>
                  <div className={styles.stepText}>
                    Add your name, bio, links, and the contact info you want visible.
                  </div>
                </div>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepBody}>
                  <div className={styles.stepTitle}>Share your link</div>
                  <div className={styles.stepText}>
                    Use it on business cards, email signatures, text messages, and socials.
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.centerCta}>
              <Link to="/buy?product=mpdId" className={styles.primaryButton}>
                View pricing
              </Link>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>FAQ</h2>
            <div className={styles.faqGrid}>
              <div className={styles.faqItem}>
                <h3 className={styles.faqQ}>Is the MPD ID Page public?</h3>
                <p className={styles.faqA}>
                  Yes. Anyone with your link can view it. You control what information is shown.
                </p>
              </div>
              <div className={styles.faqItem}>
                <h3 className={styles.faqQ}>Do I need to log in to buy it?</h3>
                <p className={styles.faqA}>
                  Yes. Purchasing a subscription is tied to your account so your ID Page stays
                  connected to you.
                </p>
              </div>
              <div className={styles.faqItem}>
                <h3 className={styles.faqQ}>Where will my page live?</h3>
                <p className={styles.faqA}>
                  Your page is available at a public URL like{' '}
                  <span className={styles.code}>mypeopledatabase.com/profile/your-id</span>.
                </p>
              </div>
              <div className={styles.faqItem}>
                <h3 className={styles.faqQ}>Can I change what’s on the page?</h3>
                <p className={styles.faqA}>
                  Yes. You can update your profile anytime.
                </p>
              </div>
            </div>
          </section>

          <section className={styles.finalCta}>
            <h2 className={styles.finalTitle}>Ready to share one link everywhere?</h2>
            <p className={styles.finalText}>
              Get your MPD ID Page subscription and publish a profile that you control.
            </p>
            <div className={styles.ctaRow}>
              <Link to="/buy?product=mpdId" className={styles.primaryButton}>
                Get MPD ID Page
              </Link>
              <Link to="/faq" className={styles.secondaryButton}>
                Read FAQ
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
